/* Capi-Europe M1A-style subtle entry motion, adapted for Leonparc.
   Never hide content, respect OS reduced-motion and Shopify editor updates. */
(() => {
  'use strict';
  const selector = [
    '.lp-hero__copy',
    '#categorieen .lp-section-heading',
    '.lp-category-card',
    '.lp-vehicle',
    '.lp-solutions-grid .lp-solution-card',
    '.lp-trust-strip',
    '.lp-confidence-card',
    '.lp-product-gallery',
    '.lp-product__summary',
    '.lp-product__details',
    '.lp-product__help'
  ].join(',');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const alreadySeen = new WeakSet();
  const activeAnimations = new Set();
  const reduced = () => reducedMotion.matches || Boolean(window.Shopify && window.Shopify.designMode);

  function enter(element) {
    if (alreadySeen.has(element)) return;
    alreadySeen.add(element);
    if (reduced() || !element.animate) return;
    const animation = element.animate(
      [{ opacity: 0.88, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 480, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'none' }
    );
    activeAnimations.add(animation);
    animation.finished.then(() => activeAnimations.delete(animation), () => activeAnimations.delete(animation));
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      observer.unobserve(target);
      enter(target);
    });
  }, { threshold: 0.07, rootMargin: '0px 0px -18px 0px' }) : null;

  function scan() {
    document.querySelectorAll(selector).forEach((element) => {
      if (alreadySeen.has(element)) return;
      if (!observer || reduced()) enter(element);
      else observer.observe(element);
    });
  }
  const stopForReducedMotion = () => {
    if (reducedMotion.matches) {
      for (const animation of activeAnimations) animation.cancel();
      activeAnimations.clear();
      if (observer) observer.disconnect();
      scan();
    }
  };
  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', stopForReducedMotion);
  else reducedMotion.addListener(stopForReducedMotion);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
  document.addEventListener('shopify:section:load', scan);
})();

/* Directional sticky Leonparc header, inspired by the compact motion language
   of the accepted Mark Donders / Web-generator navigation.
   Keep Shopify's header section in document flow to avoid layout shifts.
   Downward travel hides it; a deliberate upward gesture brings it back. */
(() => {
  'use strict';
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let header = null;
  let section = null;
  let lastY = Math.max(0, window.scrollY);
  let direction = 0;
  let travel = 0;
  let frame = 0;

  function reveal() {
    section?.removeAttribute('data-lp-header-hidden');
  }

  function interactiveHeaderOpen() {
    return Boolean(
      header?.querySelector('[data-mobile-nav][open], dialog[open]') ||
      (header?.contains(document.activeElement) &&
        document.activeElement?.matches?.(':focus-visible'))
    );
  }

  function paintScroll() {
    frame = 0;
    if (!header?.isConnected || !section?.isConnected) {
      bindHeader();
      return;
    }

    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;
    lastY = y;
    section.dataset.lpHeaderScrolled = y > 48 ? 'true' : 'false';

    if (y < Math.max(180, header.offsetHeight + 72) ||
        motionPreference.matches || interactiveHeaderOpen()) {
      direction = 0;
      travel = 0;
      reveal();
      return;
    }

    if (Math.abs(delta) < 2) return;
    const nextDirection = Math.sign(delta);
    if (direction !== nextDirection) {
      direction = nextDirection;
      travel = 0;
    }
    travel = Math.min(120, travel + Math.abs(delta));
    if (direction > 0 && travel >= 28) {
      section.dataset.lpHeaderHidden = 'true';
    } else if (direction < 0 && travel >= 12) {
      reveal();
    }
  }

  function queueScroll() {
    if (!frame) frame = window.requestAnimationFrame(paintScroll);
  }

  function bindHeader() {
    const next = document.querySelector('header.lp-header[data-header]');
    const wrapper = next?.closest('.shopify-section');
    if (!next || !wrapper) return;
    if (section && section !== wrapper) {
      section.classList.remove('lp-scroll-header');
      section.removeAttribute('data-lp-header-hidden');
      section.removeAttribute('data-lp-header-scrolled');
    }
    header = next;
    section = wrapper;
    wrapper.classList.add('lp-scroll-header');
    lastY = Math.max(0, window.scrollY);
    direction = 0;
    travel = 0;
    reveal();
    queueScroll();
  }

  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', () => { reveal(); queueScroll(); }, { passive: true });
  window.addEventListener('pageshow', bindHeader);
  document.addEventListener('focusin', (event) => {
    if (header?.contains(event.target)) reveal();
  });
  document.addEventListener('toggle', (event) => {
    if (header?.contains(event.target) && event.target.open) reveal();
  }, true);
  document.addEventListener('shopify:section:load', bindHeader);
  if (motionPreference.addEventListener) motionPreference.addEventListener('change', () => { reveal(); queueScroll(); });
  else motionPreference.addListener(() => { reveal(); queueScroll(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindHeader, { once: true });
  else bindHeader();
})();
